/**
 * Removes empty values (null, undefined, or empty strings) from an object.
 *
 * The function can either:
 * - Clean all fields when `fieldsToCheck` is not provided or empty.
 * - Clean only specified fields when `fieldsToCheck` is provided.
 *
 * @template T - The type of the input object.
 *
 * @param {T} obj - The input object to be cleaned.
 * @param {(keyof T)[]} [fieldsToCheck] - Optional list of keys to check and clean.
 * If omitted or empty, all keys will be checked.
 *
 * @returns {T} - A new object of the same type `T` with empty fields removed.
 *
 * @example
 * const payload = {
 *   name: 'John',
 *   email: '',
 *   age: null,
 *   city: 'Berlin',
 * };
 *
 * cleanFields(payload);
 * // → { name: 'John', city: 'Berlin' }
 *
 * cleanFields(payload, ['email']);
 * // → { name: 'John', age: null, city: 'Berlin' }
 */
export function cleanFields<T extends Record<string, any>>(
  obj: T,
  fieldsToCheck?: (keyof T)[],
): T {
  const shouldCheckAll = !fieldsToCheck || fieldsToCheck.length === 0;

  const cleanedEntries = Object.entries(obj).filter(([key, value]) => {
    const isTargetField =
      shouldCheckAll || fieldsToCheck!.includes(key as keyof T);

    const isEmpty =
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '');

    return !(isTargetField && isEmpty);
  });

  return Object.fromEntries(cleanedEntries) as T;
}
